import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Button, Modal, Row, Col, Badge, Alert, Nav } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database, storage } from '../firebase';
import { ref, onValue, push, remove, set, get } from 'firebase/database';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { Bike } from '../types/bike';
import { MaintenanceRecord, MAINTENANCE_TYPES, MAINTENANCE_TYPE_LABELS, MaintenanceType } from '../types/maintenance';
import { Link, useLocation } from 'react-router-dom';
import MaintenanceReceiptUploader from './MaintenanceReceiptUploader';

interface MaintenanceFormData {
  bikeId: string;
  date: string;
  type: MaintenanceType;
  description: string;
  cost: string;
  tire: string;
  tube: string;
  valveType: string;
  valveLength: string;
  receiptUrl: string;
}

const VegaMaintenance = () => {
  const { currentUser } = useAuth();
  const [bikes, setBikes] = useState<Bike[]>([]);
  const [ownedBikeIds, setOwnedBikeIds] = useState<string[]>([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentReceiptUrl, setCurrentReceiptUrl] = useState<string>('');
  const [currentRecord, setCurrentRecord] = useState<MaintenanceRecord | null>(null);
  const [originalReceiptUrl, setOriginalReceiptUrl] = useState<string>('');
  const [selectedBikeId, setSelectedBikeId] = useState<string>('');
  const [formData, setFormData] = useState<MaintenanceFormData>({
    bikeId: '',
    date: new Date().toISOString().split('T')[0],
    type: 'tune-up',
    description: '',
    cost: '',
    tire: '',
    tube: '',
    valveType: '',
    valveLength: '',
    receiptUrl: ''
  });
  const [alertMessage, setAlertMessage] = useState<{type: 'success' | 'danger', message: string} | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    // Fetch all bikes
    const bikesRef = ref(database, 'bikes');
    const bikesUnsubscribe = onValue(bikesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const bikesArray = Object.entries(data).map(([id, bike]) => ({
          id,
          ...(bike as Omit<Bike, 'id'>)
        }));
        setBikes(bikesArray);
      } else {
        setBikes([]);
      }
    });

    // Fetch owned bikes
    const ownedBikesRef = ref(database, `user_settings/${currentUser.uid}/ownedBikes`);
    const ownedBikesUnsubscribe = onValue(ownedBikesRef, (snapshot) => {
      const data = snapshot.val() || {};
      const bikeIds = Object.values(data) as string[];
      setOwnedBikeIds(bikeIds);
      
      // Don't auto-select a bike, let "Alle Velos" be the default
    });

    return () => {
      bikesUnsubscribe();
      ownedBikesUnsubscribe();
    };
  }, [currentUser, selectedBikeId]);

  // Fetch maintenance records for all owned bikes
  useEffect(() => {
    if (!currentUser || !ownedBikeIds.length) {
      setMaintenanceRecords([]);
      return;
    }

    const unsubscribes: (() => void)[] = [];

    // Create a listener for each owned bike
    ownedBikeIds.forEach(bikeId => {
      const maintenanceRef = ref(database, `bikes/${bikeId}/maintenance`);
      const unsubscribe = onValue(maintenanceRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const bikeRecords = Object.entries(data).map(([id, record]) => ({
            id,
            bikeId, // Ensure bikeId is included
            ...(record as Omit<MaintenanceRecord, 'id' | 'bikeId'>)
          }));
          
          // Update the maintenance records state properly
          setMaintenanceRecords(prev => {
            // Remove existing records for this bike and add new ones
            const filteredRecords = prev.filter(r => r.bikeId !== bikeId);
            return [...filteredRecords, ...bikeRecords];
          });
        } else {
          // Remove records for this bike if none exist
          setMaintenanceRecords(prev => prev.filter(r => r.bikeId !== bikeId));
        }
      });
      
      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [currentUser, ownedBikeIds]);

  const handleInputChange = (field: keyof MaintenanceFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      bikeId: selectedBikeId,
      date: new Date().toISOString().split('T')[0],
      type: 'tune-up',
      description: '',
      cost: '',
      tire: '',
      tube: '',
      valveType: '',
      valveLength: '',
      receiptUrl: ''
    });
  };

  const handleAddRecord = async () => {
    if (!currentUser) return;
    
    const bikeId = formData.bikeId || selectedBikeId;
    if (!bikeId) {
      setAlertMessage({
        type: 'danger',
        message: 'Bitte wählen Sie ein Velo aus.'
      });
      return;
    }

    try {
      // Update bike equipment data
      const bikeRef = ref(database, `bikes/${bikeId}`);
      const bikeSnapshot = await get(bikeRef);
      const bikeData = bikeSnapshot.val();
      
      if (bikeData) {
        const updatedBikeData = { ...bikeData };
        
        // Only update properties that have values to avoid undefined
        if (formData.tire && formData.tire.trim()) {
          updatedBikeData.tire = formData.tire;
        }
        if (formData.tube && formData.tube.trim()) {
          updatedBikeData.tube = formData.tube;
        }
        if (formData.valveType && formData.valveType.trim()) {
          updatedBikeData.valveType = formData.valveType;
        }
        if (formData.valveLength && formData.valveLength.trim()) {
          updatedBikeData.valveLength = formData.valveLength;
        }
        
        await set(bikeRef, updatedBikeData);
      }
      
      // Add maintenance record
      const maintenanceRef = ref(database, `bikes/${bikeId}/maintenance`);
      const newRecord: Omit<MaintenanceRecord, 'id' | 'bikeId'> = {
        date: formData.date,
        type: formData.type,
        description: formData.description,
        cost: parseFloat(formData.cost) || 0,
        completed: true
      };
      
      // Only add receiptUrl if it has a value to avoid undefined
      if (formData.receiptUrl && formData.receiptUrl.trim()) {
        newRecord.receiptUrl = formData.receiptUrl;
      }
      
      await push(maintenanceRef, newRecord);
      setShowAddModal(false);
      resetForm();
      setAlertMessage({
        type: 'success',
        message: 'Wartungseintrag erfolgreich hinzugefügt!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Hinzufügen des Wartungseintrags.'
      });
    }
  };

  const handleEditRecord = async () => {
    if (!currentUser || !currentRecord) return;

    try {
      // Update bike equipment data
      const bikeRef = ref(database, `bikes/${currentRecord.bikeId}`);
      const bikeSnapshot = await get(bikeRef);
      const bikeData = bikeSnapshot.val();
      
      if (bikeData) {
        const updatedBikeData = { ...bikeData };
        
        // Only update properties that have values to avoid undefined
        if (formData.tire && formData.tire.trim()) {
          updatedBikeData.tire = formData.tire;
        }
        if (formData.tube && formData.tube.trim()) {
          updatedBikeData.tube = formData.tube;
        }
        if (formData.valveType && formData.valveType.trim()) {
          updatedBikeData.valveType = formData.valveType;
        }
        if (formData.valveLength && formData.valveLength.trim()) {
          updatedBikeData.valveLength = formData.valveLength;
        }
        
        await set(bikeRef, updatedBikeData);
      }
      
      // Update maintenance record
      const recordRef = ref(database, `bikes/${currentRecord.bikeId}/maintenance/${currentRecord.id}`);
      const updatedRecord: Omit<MaintenanceRecord, 'id' | 'bikeId'> = {
        date: formData.date,
        type: formData.type as MaintenanceType,
        description: formData.description,
        cost: parseFloat(formData.cost) || 0,
        completed: currentRecord.completed
      };
      
      // Handle receipt file cleanup and update
      if (formData.receiptUrl !== originalReceiptUrl) {
        // Delete old receipt file if it exists and a new one was uploaded
        if (originalReceiptUrl && formData.receiptUrl && formData.receiptUrl.trim()) {
          try {
            const fileUrlPath = originalReceiptUrl.split('?')[0].split('/o/')[1];
            if (fileUrlPath) {
              const decodedPath = decodeURIComponent(fileUrlPath);
              const oldFileRef = storageRef(storage, decodedPath);
              await deleteObject(oldFileRef);
            }
          } catch (deleteError) {
            console.error('Error deleting old receipt file:', deleteError);
          }
        }
      }
      
      // Only add receiptUrl if it has a value to avoid undefined
      if (formData.receiptUrl && formData.receiptUrl.trim()) {
        updatedRecord.receiptUrl = formData.receiptUrl;
      }
      
      await set(recordRef, updatedRecord);
      setShowEditModal(false);
      setCurrentRecord(null);
      setAlertMessage({
        type: 'success',
        message: 'Wartungseintrag erfolgreich aktualisiert!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating maintenance record:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Aktualisieren des Wartungseintrags.'
      });
    }
  };

  const handleDeleteRecord = async (record: MaintenanceRecord) => {
    if (!currentUser || !window.confirm('Sind Sie sicher, dass Sie diesen Wartungseintrag löschen möchten?')) return;

    try {
      // Delete receipt file from storage if it exists
      if (record.receiptUrl) {
        try {
          // Extract the path from the URL
          const fileUrlPath = record.receiptUrl.split('?')[0].split('/o/')[1];
          if (fileUrlPath) {
            const decodedPath = decodeURIComponent(fileUrlPath);
            const fileRef = storageRef(storage, decodedPath);
            await deleteObject(fileRef);
          }
        } catch (storageError) {
          console.error('Error deleting receipt file:', storageError);
          // Continue with record deletion even if file deletion fails
        }
      }

      // Delete maintenance record from database
      const recordRef = ref(database, `bikes/${record.bikeId}/maintenance/${record.id}`);
      await remove(recordRef);
      setAlertMessage({
        type: 'success',
        message: 'Wartungseintrag und Beleg erfolgreich gelöscht!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error deleting maintenance record:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Löschen des Wartungseintrags.'
      });
    }
  };

  const handleToggleComplete = async (record: MaintenanceRecord) => {
    if (!currentUser) return;

    try {
      const recordRef = ref(database, `bikes/${record.bikeId}/maintenance/${record.id}`);
      const updatedRecord: Omit<MaintenanceRecord, 'id' | 'bikeId'> = {
        date: record.date,
        type: record.type,
        description: record.description,
        cost: record.cost,
        completed: !record.completed,
        receiptUrl: record.receiptUrl
      };
      await set(recordRef, updatedRecord);
    } catch (error) {
      console.error('Error toggling maintenance record completion:', error);
    }
  };

  const openEditModal = (record: MaintenanceRecord) => {
    setCurrentRecord(record);
    const bike = bikes.find(b => b.id === record.bikeId);
    const receiptUrl = record.receiptUrl || '';
    setOriginalReceiptUrl(receiptUrl);
    setFormData({
      bikeId: record.bikeId,
      date: record.date,
      type: record.type as MaintenanceType,
      description: record.description,
      cost: record.cost.toString(),
      tire: bike?.tire || '',
      tube: bike?.tube || '',
      valveType: bike?.valveType || '',
      valveLength: bike?.valveLength || '',
      receiptUrl: receiptUrl
    });
    setShowEditModal(true);
  };

  const getBikeName = (bikeId: string) => {
    const bike = bikes.find(b => b.id === bikeId);
    return bike ? `${bike.brand} ${bike.model} (${bike.color})` : 'Unbekanntes Velo';
  };

  // Filter records for the selected bike
  const filteredRecords = selectedBikeId 
    ? maintenanceRecords.filter(record => record.bikeId === selectedBikeId)
    : maintenanceRecords;

  // Sort records by date (newest first)
  const sortedRecords = [...filteredRecords].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  if (!currentUser) {
    return (
      <Card>
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>VeGa Wartung</Card.Title>
          <p>Bitte melden Sie sich an, um Wartungseinträge anzuzeigen.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="container">
      <h2 className="mb-4">Velo Wartung</h2>

      {/* Bike section tabs, shown only on /bike, /bike/maintenance, /bike/settings */}
      {['/bike', '/bike/maintenance', '/bike/settings'].includes(location.pathname) && (
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link as={Link} to="/bike" active={location.pathname === '/bike'}>
              Velos
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={Link} to="/bike/maintenance" active={location.pathname === '/bike/maintenance'}>
              Wartung
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={Link} to="/bike/settings" active={location.pathname === '/bike/settings'}>
              Konfiguration
            </Nav.Link>
          </Nav.Item>
        </Nav>
      )}

      {alertMessage && (
        <Alert variant={alertMessage.type} onClose={() => setAlertMessage(null)} dismissible>
          {alertMessage.message}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title style={{ color: '#1780a6' }}>Wartungseinträge</Card.Title>
            <Button 
              variant="primary" 
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
            >
              Neue Wartung hinzufügen
            </Button>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Velo auswählen</Form.Label>
            <Form.Select 
              value={selectedBikeId} 
              onChange={(e) => setSelectedBikeId(e.target.value)}
            >
              <option value="">Alle Velos</option>
              {bikes
                .filter(bike => ownedBikeIds.includes(bike.id))
                .map(bike => (
                  <option key={bike.id} value={bike.id}>
                    {bike.brand} {bike.model} ({bike.color})
                  </option>
                ))}
            </Form.Select>
          </Form.Group>

          {sortedRecords.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Velo</th>
                  <th>Typ</th>
                  <th>Beschreibung</th>
                  <th>Kosten (CHF)</th>
                  <th>Beleg</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sortedRecords.map(record => (
                  <tr key={record.id}>
                    <td>{record.date}</td>
                    <td>{getBikeName(record.bikeId)}</td>
                    <td>{MAINTENANCE_TYPE_LABELS[record.type as MaintenanceType]}</td>
                    <td>{record.description}</td>
                    <td>{record.cost.toFixed(2)}</td>
                    <td>
                      {record.receiptUrl ? (
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="p-0"
                          onClick={() => {
                            setCurrentReceiptUrl(record.receiptUrl!);
                            setShowReceiptModal(true);
                          }}
                        >
                          <i className="bi bi-file-earmark-pdf text-danger me-1"></i>
                          Beleg
                        </Button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <Badge 
                        bg={record.completed ? 'success' : 'warning'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleToggleComplete(record)}
                      >
                        {record.completed ? 'Abgeschlossen' : 'Ausstehend'}
                      </Badge>
                    </td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => openEditModal(record)}
                      >
                        Bearbeiten
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteRecord(record)}
                      >
                        Löschen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">Keine Wartungseinträge gefunden.</p>
          )}
        </Card.Body>
      </Card>

      {/* Add Maintenance Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neue Wartung hinzufügen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Velo</Form.Label>
              <Form.Select 
                value={formData.bikeId || selectedBikeId} 
                onChange={(e) => handleInputChange('bikeId', e.target.value)}
                required
              >
                <option value="">Velo auswählen...</option>
                {bikes
                  .filter(bike => ownedBikeIds.includes(bike.id))
                  .map(bike => (
                    <option key={bike.id} value={bike.id}>
                      {bike.brand} {bike.model} ({bike.color})
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Datum</Form.Label>
              <Form.Control 
                type="date" 
                value={formData.date} 
                onChange={(e) => handleInputChange('date', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Wartungstyp</Form.Label>
              <Form.Select 
                value={formData.type} 
                onChange={(e) => handleInputChange('type', e.target.value)}
                required
              >
                {MAINTENANCE_TYPES.map(type => (
                  <option key={type} value={type}>
                    {MAINTENANCE_TYPE_LABELS[type]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={formData.description} 
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kosten (CHF)</Form.Label>
              <Form.Control 
                type="number" 
                step="0.01" 
                min="0" 
                value={formData.cost} 
                onChange={(e) => handleInputChange('cost', e.target.value)}
                required
              />
            </Form.Group>

            <MaintenanceReceiptUploader
              bikeId={formData.bikeId || selectedBikeId}
              existingUrl={formData.receiptUrl}
              onUploadComplete={(url) => handleInputChange('receiptUrl', url)}
              onDelete={() => handleInputChange('receiptUrl', '')}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleAddRecord}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Maintenance Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Wartungseintrag bearbeiten</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Velo</Form.Label>
              <Form.Control 
                type="text" 
                value={getBikeName(formData.bikeId)}
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Datum</Form.Label>
              <Form.Control 
                type="date" 
                value={formData.date} 
                onChange={(e) => handleInputChange('date', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Wartungstyp</Form.Label>
              <Form.Select 
                value={formData.type} 
                onChange={(e) => handleInputChange('type', e.target.value)}
                required
              >
                {MAINTENANCE_TYPES.map(type => (
                  <option key={type} value={type}>
                    {MAINTENANCE_TYPE_LABELS[type]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={formData.description} 
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kosten (CHF)</Form.Label>
              <Form.Control 
                type="number" 
                step="0.01" 
                min="0" 
                value={formData.cost} 
                onChange={(e) => handleInputChange('cost', e.target.value)}
                required
              />
            </Form.Group>

            <MaintenanceReceiptUploader
              bikeId={formData.bikeId}
              existingUrl={formData.receiptUrl}
              onUploadComplete={(url) => handleInputChange('receiptUrl', url)}
              onDelete={() => handleInputChange('receiptUrl', '')}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            // Delete new receipt file if it was uploaded but not saved
            if (formData.receiptUrl && formData.receiptUrl !== originalReceiptUrl) {
              try {
                const fileUrlPath = formData.receiptUrl.split('?')[0].split('/o/')[1];
                if (fileUrlPath) {
                  const decodedPath = decodeURIComponent(fileUrlPath);
                  const newFileRef = storageRef(storage, decodedPath);
                  deleteObject(newFileRef).catch(error => 
                    console.error('Error deleting unsaved receipt file:', error)
                  );
                }
              } catch (error) {
                console.error('Error parsing receipt URL for deletion:', error);
              }
            }
            setShowEditModal(false);
          }}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleEditRecord}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Receipt Viewer Modal */}
      <Modal 
        show={showReceiptModal} 
        onHide={() => setShowReceiptModal(false)} 
        size="lg" 
        centered
        className="file-viewer-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Wartungsbeleg</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {currentReceiptUrl && (
            <div className="pdf-container">
              <div className="ratio ratio-16x9" style={{ minHeight: '500px' }}>
                <iframe 
                  src={`${currentReceiptUrl}#toolbar=1&navpanes=1&scrollbar=1`} 
                  title="PDF Viewer" 
                  width="100%" 
                  height="100%" 
                  frameBorder="0"
                  allowFullScreen
                >
                  <p>Ihr Browser unterstützt keine eingebetteten PDFs. <a href={currentReceiptUrl} target="_blank" rel="noopener noreferrer">PDF herunterladen</a></p>
                </iframe>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReceiptModal(false)}>
            Schließen
          </Button>
          <Button 
            variant="primary" 
            href={currentReceiptUrl} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            In neuem Tab öffnen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VegaMaintenance;
